/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import cx from 'classnames';
import { useEffect, useRef } from 'react';
import { accessibilityHelper } from 'helpers';
import { ELSButton } from 'components/common/els';

interface FlyoutProps {
  isOpen: boolean;
  content: JSX.Element | null;
  closeFlyout: () => void;
}

const trapTabKeyWithin = (containerEl: HTMLDivElement | null, event: React.KeyboardEvent<HTMLDivElement>) => {
  const focusableNodeList = containerEl?.querySelectorAll<HTMLElement>(accessibilityHelper.FOCUSABLE_ELEMENT_SELECTORS);
  if (!focusableNodeList || focusableNodeList.length === 0) {
    return;
  }

  const focusableElements = Array.prototype.slice.call(focusableNodeList) as HTMLElement[];
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const currentActiveElement = document.activeElement as HTMLElement | null;
  if (!currentActiveElement || !containerEl?.contains(currentActiveElement)) {
    return;
  }

  if (!event.shiftKey && currentActiveElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  } else if (event.shiftKey && currentActiveElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  }
};

const Flyout = (props: FlyoutProps) => {
  const { content, isOpen, closeFlyout } = props;
  const showContent = isOpen ? 'show' : 'hide';
  const flyoutRef = useRef<HTMLDivElement>(null);

  const handleKeyboardFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    accessibilityHelper.handleEscapeKey(event, () => closeFlyout());
    if (event.key !== 'Tab') {
      return;
    }
    trapTabKeyWithin(flyoutRef.current, event);
  };

  useEffect(() => {
    if (!isOpen) return;

    const firstFocusableElement = flyoutRef.current?.querySelector<HTMLElement>(accessibilityHelper.FOCUSABLE_ELEMENT_SELECTORS);
    window.setTimeout(() => {
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }, 50);
  }, [isOpen]);

  return (
    <div ref={flyoutRef} onKeyDown={handleKeyboardFocus} role="dialog" aria-modal="true" tabIndex={-1} className={cx('flyout flyout__content', showContent)}>
      <div className="sticky-flex-container">
        <div className="u-els-padding-2x u-els-padding-1x@desktop u-els-padding-top-1o2">
          <ELSButton className="flyout__close-button" type="secondary" text="Close" onClick={() => closeFlyout()} preventAutoPending />
        </div>
        <div className="sticky-flex-container__content u-els-padding-top-1o2">{content}</div>
      </div>
    </div>
  );
};

export default Flyout;
